import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { NavBar } from '../../components/NavBar';
import { api } from '../../lib/api';
import type { Home, Task } from '@home-guard/shared';

const CURRENT_YEAR = new Date().getFullYear();

interface ApplianceRow {
  id: string;
  type: string;
  make: string | null;
  model: string | null;
  install_year: number | null;
}

const APPLIANCE_INFO: Record<string, { label: string; lifespan: number; costMin: number; costMax: number }> = {
  hvac:          { label: 'HVAC System',    lifespan: 15, costMin: 5000,  costMax: 10000 },
  water_heater:  { label: 'Water Heater',   lifespan: 10, costMin: 800,   costMax: 1500  },
  washer:        { label: 'Washer',         lifespan: 11, costMin: 500,   costMax: 1200  },
  dryer:         { label: 'Dryer',          lifespan: 13, costMin: 500,   costMax: 1200  },
  refrigerator:  { label: 'Refrigerator',   lifespan: 12, costMin: 1000,  costMax: 2000  },
  dishwasher:    { label: 'Dishwasher',     lifespan:  9, costMin: 600,   costMax: 1200  },
  ac_window:     { label: 'Window AC',      lifespan: 10, costMin: 300,   costMax: 700   },
  ac_mini_split: { label: 'Mini Split AC',  lifespan: 20, costMin: 2000,  costMax: 5000  },
  fireplace:     { label: 'Fireplace',      lifespan: 40, costMin: 2000,  costMax: 8000  },
};

const DEDUCTIONS: Record<string, number> = {
  critical: 15, high: 8, medium: 4, low: 2,
};

function ageStatus(age: number, lifespan: number): 'danger' | 'warn' | 'ok' {
  if (age >= lifespan) return 'danger';
  if (age >= lifespan - 2) return 'warn';
  return 'ok';
}

function ApplianceCostRow({ appliance }: { appliance: ApplianceRow }) {
  const info = APPLIANCE_INFO[appliance.type];
  if (!info) return null;
  const age = appliance.install_year ? CURRENT_YEAR - appliance.install_year : null;
  if (age === null) return null;

  const status = ageStatus(age, info.lifespan);
  const pct = Math.min(100, (age / info.lifespan) * 100);
  const barColor = status === 'danger' ? 'bg-red-500' : status === 'warn' ? 'bg-amber-500' : 'bg-green-500';
  const textColor = status === 'danger' ? 'text-red-600' : status === 'warn' ? 'text-amber-600' : 'text-gray-500';
  const label = [appliance.make, appliance.model].filter(Boolean).join(' ') || info.label;

  return (
    <div className="py-3 border-b border-gray-100 last:border-0">
      <div className="flex justify-between items-start mb-1.5">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{label}</p>
          <p className={`text-xs ${textColor} font-medium`}>
            {age} yr{age === 1 ? '' : 's'} old · {info.lifespan} yr lifespan
            {status === 'danger' && ' · Replace soon'}
          </p>
        </div>
        <p className="text-sm font-semibold text-gray-700 ml-3 flex-shrink-0">
          ${info.costMin.toLocaleString()}+
        </p>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function Insights() {
  const navigate = useNavigate();

  const { data: homesData, isLoading: homesLoading } = useQuery({
    queryKey: ['homes'],
    queryFn: () => api.get<{ data: Home[] }>('/homes'),
  });
  const homes = homesData?.data ?? [];
  const home = homes.find(h => h.is_primary) ?? homes[0];

  const { data: tasksData } = useQuery({
    queryKey: ['tasks', home?.id ?? '', 'all'],
    queryFn: () => api.get<{ data: Task[] }>(`/homes/${home!.id}/tasks`),
    enabled: !!home,
  });

  const { data: appliancesData } = useQuery({
    queryKey: ['appliances', home?.id ?? ''],
    queryFn: () => api.get<{ data: ApplianceRow[] }>(`/homes/${home!.id}/appliances`),
    enabled: !!home,
  });

  const tasks = tasksData?.data ?? [];
  const appliances = appliancesData?.data ?? [];

  const today = new Date();
  today.setHours(0,0,0,0);
  const thisYearStart = new Date(CURRENT_YEAR, 0, 1).toISOString();

  const pendingTasks = tasks.filter(t => !t.is_complete && !t.snoozed_until);
  const overdueTasks = tasks.filter(t => !t.is_complete && t.due_date < today.toISOString().slice(0,10));
  const completedThisYear = tasks.filter(t => t.is_complete && t.completed_at && t.completed_at >= thisYearStart);

  // How many overdue tasks are hurting the score
  const totalDeductions = overdueTasks.reduce((acc, t) => acc + (DEDUCTIONS[t.urgency] ?? 2), 0);
  const tasksToNextTen = Math.ceil((home?.health_score ?? 0) % 10 === 0
    ? 0
    : ((home?.health_score ?? 0) % 10) / (DEDUCTIONS['medium'] ?? 4));

  // Cost forecast
  const trackableAppliances = appliances.filter(a => APPLIANCE_INFO[a.type] && a.install_year);
  const totalReplacementCost = trackableAppliances.reduce((acc, a) => {
    const info = APPLIANCE_INFO[a.type];
    return info ? acc + info.costMin : acc;
  }, 0);
  const urgentAppliances = trackableAppliances.filter(a => {
    const info = APPLIANCE_INFO[a.type];
    if (!info || !a.install_year) return false;
    const age = CURRENT_YEAR - a.install_year;
    return age >= info.lifespan - 2;
  });

  const scoreColor = (home?.health_score ?? 0) >= 90 ? 'bg-green-500'
    : (home?.health_score ?? 0) >= 70 ? 'bg-blue-600'
    : (home?.health_score ?? 0) >= 50 ? 'bg-amber-500'
    : 'bg-red-500';

  if (homesLoading) {
    return (
      <div className="min-h-dvh flex items-center justify-center">
        <div className="w-8 h-8 border-[3px] border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!home) {
    navigate('/app/home/setup', { replace: true });
    return null;
  }

  return (
    <div className="min-h-dvh bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-700 to-blue-900 pt-12 pb-5 px-4">
        <h1 className="text-white font-bold text-xl">Insights</h1>
        <p className="text-white/60 text-sm mt-0.5">{home.address_line1}</p>
      </div>

      <div className="px-4 pt-4 space-y-4">
        {/* Health score card */}
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-bold text-gray-900">Home Health Score</p>
            <span className="text-2xl font-bold text-gray-900">{home.health_score}</span>
          </div>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden mb-2">
            <div
              className={`h-full rounded-full transition-all ${scoreColor}`}
              style={{ width: `${home.health_score}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-400">
            <span>0</span>
            <span>100</span>
          </div>

          {overdueTasks.length > 0 && (
            <div className="mt-3 bg-red-50 rounded-xl px-3 py-2.5">
              <p className="text-sm text-red-700 font-medium">
                {overdueTasks.length} overdue task{overdueTasks.length === 1 ? '' : 's'} reducing your score by {totalDeductions} point{totalDeductions === 1 ? '' : 's'}
              </p>
            </div>
          )}
          {overdueTasks.length === 0 && tasksToNextTen > 0 && (
            <p className="text-xs text-gray-500 mt-2">
              Complete {tasksToNextTen} more task{tasksToNextTen === 1 ? '' : 's'} to reach the next 10-point milestone
            </p>
          )}
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-2xl shadow-sm p-3 text-center">
            <p className="text-xl font-bold text-blue-600">{pendingTasks.length}</p>
            <p className="text-xs text-gray-500 mt-0.5">Pending</p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm p-3 text-center">
            <p className={`text-xl font-bold ${overdueTasks.length > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {overdueTasks.length}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">Overdue</p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm p-3 text-center">
            <p className="text-xl font-bold text-green-600">{completedThisYear.length}</p>
            <p className="text-xs text-gray-500 mt-0.5">Done this year</p>
          </div>
        </div>

        {/* Estimated savings */}
        {completedThisYear.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
            <p className="text-sm font-bold text-green-800 mb-1">Maintenance savings</p>
            <p className="text-2xl font-bold text-green-700">
              ~${(completedThisYear.length * 200).toLocaleString()}
            </p>
            <p className="text-xs text-green-600 mt-1">
              Estimated savings vs emergency repair costs from {completedThisYear.length} completed task{completedThisYear.length === 1 ? '' : 's'} this year
            </p>
          </div>
        )}

        {/* Appliance cost forecast */}
        {trackableAppliances.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-4">
            <div className="flex justify-between items-center mb-1">
              <p className="text-sm font-bold text-gray-900">Appliance Forecast</p>
              <p className="text-sm font-semibold text-gray-500">${totalReplacementCost.toLocaleString()}+</p>
            </div>
            {urgentAppliances.length > 0 && (
              <p className="text-xs text-amber-600 font-medium mb-3">
                {urgentAppliances.length} appliance{urgentAppliances.length === 1 ? '' : 's'} nearing end of lifespan
              </p>
            )}
            {trackableAppliances.map(a => (
              <ApplianceCostRow key={a.id} appliance={a} />
            ))}
          </div>
        )}

        {trackableAppliances.length === 0 && appliances.length === 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-6 text-center">
            <p className="text-3xl mb-2">🔧</p>
            <p className="text-sm font-semibold text-gray-700 mb-1">Add appliances for cost forecasting</p>
            <p className="text-xs text-gray-500 mb-3">Track your HVAC, water heater, and more to see replacement cost estimates.</p>
            <button
              onClick={() => navigate('/app/appliances')}
              className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold"
              style={{ minHeight: 44 }}
            >
              Add appliances
            </button>
          </div>
        )}
      </div>

      <NavBar />
    </div>
  );
}
